import * as debugLog from './debug-log'
import * as reportError from 'airship/modules/error-reporting'

// asc ~ airship cache
const PREFIX = 'asc'

export enum Duration {
  Second = 1000,
  Minute = 60000,
  Hour = 3600000
}

interface CachedItem<T> {
  value: T
  expiresAt?: number
}

const storage = window.sessionStorage

/**
 * Sets a cache value. Returns true if it was successful and false otherwise.
 */
export function set<T>(key: string, value: T, expiryMs?: number): boolean {
  debugLog.log('Caching %s for %s with val', key, expiryMs, value)
  if (!storage) {
    return false
  }
  const item: CachedItem<T> = {value}
  if (expiryMs) {
    item.expiresAt = new Date().getTime() + expiryMs
  }
  storage.setItem(prefixedKey(key), JSON.stringify(item))

  return true
}

export function get<T>(key: string): T | undefined {
  if (!storage) {
    return
  }

  const rawItem = storage.getItem(prefixedKey(key))
  if (!rawItem) {
    debugLog.log('No cached value found for key', key)
    return
  }

  let item: CachedItem<T> | undefined
  try {
    item = JSON.parse(rawItem)
  } catch (err) {
    reportError(err, __filename)
  }

  if (!item || !item.value) {
    debugLog.log('No cached value found for key', key)
    return
  }
  if (!isCacheItem(item)) {
    debugLog.log('Cached item was not a CachedItem', item)
    return
  }
  if (isExpired(item.expiresAt)) {
    debugLog.log('Cached value expired')
    return
  }

  debugLog.log('Returning cached value', item.value)
  return item.value
}

function isCacheItem<T>(item: CachedItem<T>): item is CachedItem<T> {
  return item && Boolean(item.value)
}

function isExpired(ts?: number): boolean {
  if (!ts) {
    return false
  }
  return ts <= new Date().getTime()
}

function prefixedKey(key: string): string {
  return PREFIX + key
}
